// src/components/HighlightedCreativeText.jsx
import React from "react";
import { WRITING_SIGNAL_UI, resolveSignalName } from "./writingSignalUI";

const HIGHLIGHT_PRIORITY = [
  "ws-highlight--amber-dark",
  "ws-highlight--indigo-medium",
  "ws-highlight--purple-medium",
  "ws-highlight--red-light",
  "ws-highlight--teal-medium",
  "ws-highlight--orange-medium",
  "ws-highlight--green-medium",
  "ws-highlight--blue-dark",
];

const FAMILY_ORDER = ["Blue", "Green", "Pink", "Purple", "Orange", "Teal", "Indigo", "Amber"];
const FAMILY_CLASS_MAP = {
  Blue: "ws-family-key--blue",
  Green: "ws-family-key--green",
  Pink: "ws-family-key--red",
  Purple: "ws-family-key--purple",
  Orange: "ws-family-key--orange",
  Teal: "ws-family-key--teal",
  Indigo: "ws-family-key--indigo",
  Amber: "ws-family-key--amber",
};

function resolveHighlightClass(active) {
  const classes = active.map((ann) => ann.highlightClass).filter(Boolean);
  return HIGHLIGHT_PRIORITY.find((name) => classes.includes(name)) || classes[0] || "ws-highlight--blue-medium";
}

function buildLegendItems(normalized) {
  const familySignals = new Map();
  normalized.forEach((ann) => {
    const ui = WRITING_SIGNAL_UI[ann.signalName];
    const family = String(ui?.family || "").trim();
    if (!family) return;
    if (!familySignals.has(family)) familySignals.set(family, new Set());
    familySignals.get(family).add(ann.signalName);
  });

  return FAMILY_ORDER.flatMap((family) => {
    const signalSet = familySignals.get(family);
    if (!signalSet || signalSet.size === 0) return [];
    const firstSignal = [...signalSet][0];
    const ui = WRITING_SIGNAL_UI[firstSignal];
    const label = String(ui?.familyLabel || ui?.label || family).trim();
    return [{ family, label, className: `ws-family-key ${FAMILY_CLASS_MAP[family] || ""}`.trim() }];
  });
}

export default function HighlightedCreativeText({
  text,
  annotations,
  showInlineHighlights = false,
}) {
  const cleanText = String(text || "");
  const anns = Array.isArray(annotations) ? [...annotations] : [];

  if (!cleanText || !showInlineHighlights || anns.length === 0) {
    return <div className="student-text-highlighted">{cleanText}</div>;
  }

  // Normalize spans and remove empty / invalid annotations.
  const normalized = anns
    .map((ann) => {
      const start = Math.max(0, Math.min(cleanText.length, Number(ann?.start) || 0));
      const end = Math.max(start, Math.min(cleanText.length, Number(ann?.end) || start));
      const signalName = resolveSignalName(ann?.signal || ann?.label);
      const ui = WRITING_SIGNAL_UI[signalName];
      if (!ui || ui.inline !== true) return null;
      return {
        start,
        end,
        signalName,
        displayName: ui.label || "Highlight",
        highlightClass: ui.highlightClass || "ws-highlight--blue-medium",
        labelClass: ui.labelClass || "ws-inline-label ws-inline-label--blue",
      };
    })
    .filter((ann) => ann && ann.end > ann.start);

  if (normalized.length === 0) {
    return <div className="student-text-highlighted">{cleanText}</div>;
  }

  const legendItems = buildLegendItems(normalized);

  // Build boundaries so each text segment is rendered once, with all active labels.
  const boundarySet = new Set([0, cleanText.length]);
  normalized.forEach((ann) => {
    boundarySet.add(ann.start);
    boundarySet.add(ann.end);
  });
  const boundaries = Array.from(boundarySet).sort((a, b) => a - b);

  const chunks = [];
  for (let i = 0; i < boundaries.length - 1; i += 1) {
    const start = boundaries[i];
    const end = boundaries[i + 1];
    if (end <= start) continue;

    const segmentText = cleanText.slice(start, end);
    const active = normalized.filter((ann) => ann.start <= start && ann.end >= end);

    if (active.length === 0) {
      chunks.push({
        type: "plain",
        key: `plain-${start}-${end}`,
        text: segmentText,
      });
      continue;
    }

    const seen = new Set();
    const labels = [];
    active.forEach((ann) => {
      if (seen.has(ann.signalName)) return;
      seen.add(ann.signalName);
      labels.push({
        signalName: ann.signalName,
        displayName: ann.displayName,
        labelClass: ann.labelClass,
      });
    });

    const highlightTone = resolveHighlightClass(active);
    const className = `ws-highlight ${highlightTone}`;

    chunks.push({
      type: "tag",
      key: `ws-${start}-${end}`,
      text: segmentText,
      className,
      labels,
    });
  }

  return (
    <div className="student-text-highlighted">
      {chunks.map((chunk) =>
        chunk.type === "plain" ? (
          <span key={chunk.key}>{chunk.text}</span>
        ) : (
          <span
            key={chunk.key}
            className={chunk.className}
            title={chunk.labels.map((l) => l.displayName).join(", ")}
          >
            <span className="ws-highlight-text">{chunk.text}</span>
            {chunk.labels.map((label) => (
              <span
                key={`${chunk.key}-${label.signalName}`}
                className={label.labelClass || "ws-inline-label ws-inline-label--blue"}
              >
                {label.displayName}
              </span>
            ))}
          </span>
        )
      )}
      {legendItems.length > 0 ? (
        <div className="ws-signal-family-legend" aria-label="Writing Signals colour legend">
          {legendItems.map((item) => (
            <span key={item.family} className={item.className}>
              {item.family} = {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
